import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ShieldAlert } from 'lucide-react';
import { getMe, type Me } from '../lib/session';

type Role = 'member' | 'board' | 'admin';
const RANK: Record<Role, number> = { member: 1, board: 2, admin: 3 };

type State = { kind: 'loading' } | { kind: 'ok'; user: Me } | { kind: 'forbidden'; user: Me } | { kind: 'error' } | { kind: 'anon' };

/**
 * Spec 004 — client-side role gate (defence-in-depth; the server is the real authority via RolesGuard).
 * Loads the current identity, redirects an unauthenticated visitor to sign in, shows a specific
 * "not permitted" state when the role is insufficient (never a blank page), and otherwise renders the
 * page with the resolved user. Loading and error states are handled here so each page stays focused.
 */
export function RequireRole({ min, children }: { min: Role; children: (user: Me) => ReactNode }) {
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let active = true;
    getMe().then((r) => {
      if (!active) return;
      if (r.kind === 'ok') {
        setState(RANK[r.user.role] >= RANK[min] ? { kind: 'ok', user: r.user } : { kind: 'forbidden', user: r.user });
      } else if (r.kind === 'unauthenticated') {
        setState({ kind: 'anon' });
      } else {
        setState({ kind: 'error' });
      }
    });
    return () => {
      active = false;
    };
  }, [min]);

  useEffect(() => {
    if (state.kind === 'anon') navigate('/login', { replace: true });
  }, [state.kind, navigate]);

  if (state.kind === 'loading') {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col items-center px-6 py-24" aria-live="polite">
        <Loader2 aria-hidden className="h-8 w-8 animate-spin text-accent" />
        <p className="mt-4 font-serif text-lg text-ink">Loading…</p>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="mx-auto w-full max-w-md px-6 py-24">
        <p role="alert" className="flex items-start gap-2 rounded-md border border-error/40 bg-error/5 px-3 py-2 font-sans text-sm text-error">
          <ShieldAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
          We couldn’t confirm your access. Please refresh, or sign in again.
        </p>
      </div>
    );
  }

  if (state.kind === 'forbidden') {
    return (
      <div className="mx-auto w-full max-w-lg px-6 py-24 text-center">
        <ShieldAlert aria-hidden className="mx-auto h-10 w-10 text-muted" />
        <h1 className="mt-4 font-display text-2xl font-semibold uppercase tracking-[0.12em] text-ink">
          Not permitted
        </h1>
        <p className="mt-3 font-serif text-lg leading-relaxed text-muted">
          This area is for the {min} and above. You’re signed in as a {state.user.role}. If you think this
          is a mistake, ask an administrator.
        </p>
      </div>
    );
  }

  if (state.kind === 'anon') return null; // redirecting
  return <>{children(state.user)}</>;
}
